import { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { X, MapPin, Phone, ShieldCheck, Building2, Star, Check, ArrowLeft } from "lucide-react";
import { CITIES } from "@/data/cities";
import { COMPANY, IMAGES } from "@/data/content";
import Header from "@/components/site/Header";
import Services from "@/components/site/Services";
import WhyChooseUs from "@/components/site/WhyChooseUs";
import Testimonials from "@/components/site/Testimonials";
import FAQ from "@/components/site/FAQ";
import CTABand from "@/components/site/CTABand";
import Footer from "@/components/site/Footer";
import StickyCallBar from "@/components/site/StickyCallBar";
import QuoteForm from "@/components/site/QuoteForm";

const SITE_URL = "https://honeybeeroofers.com";

function CitySeo({ city }) {
  useEffect(() => {
    const title = `${city.tagline} | Roof Repair & Replacement | HoneyBee Roofers`;
    document.title = title;

    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const desc = `${city.tagline}: roof replacement, storm damage & roof leak repair, free inspections, siding & gutters in ${city.name}, ${city.state} (${city.region}). Flat-rate pricing, 50-year CertainTeed guarantee. Call ${COMPANY.phone}.`;
    setMeta("name", "description", desc);
    setMeta("name", "keywords", city.keywords);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", `${SITE_URL}/roofing/${city.slug}`);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", `${SITE_URL}/roofing/${city.slug}`);

    const ld = {
      "@context": "https://schema.org",
      "@type": "RoofingContractor",
      name: `${COMPANY.name} — ${city.name}, ${city.state}`,
      image: `${SITE_URL}/honeybee-logo.svg`,
      url: `${SITE_URL}/roofing/${city.slug}`,
      telephone: COMPANY.phone,
      priceRange: "$$",
      areaServed: { "@type": "City", name: `${city.name}, ${city.state}` },
      address: { "@type": "PostalAddress", streetAddress: "118 SE Everett Mall Way", addressLocality: "Everett", addressRegion: "WA", postalCode: "98208", addressCountry: "US" },
      aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "7" },
    };
    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: city.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    };
    let script = document.getElementById("ld-json");
    if (!script) { script = document.createElement("script"); script.id = "ld-json"; script.type = "application/ld+json"; document.head.appendChild(script); }
    script.textContent = JSON.stringify([ld, faqLd]);
  }, [city]);
  return null;
}

export default function CityPage() {
  const { slug } = useParams();
  const city = CITIES[slug];
  const [modal, setModal] = useState(false);
  const openQuote = () => setModal(true);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!city) return <Navigate to="/" replace />;

  return (
    <div className="bg-obsidian min-h-screen">
      <CitySeo city={city} />
      <Header onQuote={openQuote} />

      {/* Local hero */}
      <section id="top" className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.hero} alt={`Roofing in ${city.name}, ${city.state}`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Link to="/" data-testid="back-home" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-honey mb-5 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to home
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-honey/30 bg-honey/10 mb-5">
              <MapPin className="w-3.5 h-3.5 text-honey" />
              <span className="text-xs font-semibold tracking-wide text-honey">{city.region}</span>
            </div>
            <h1 className="font-display font-black uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
              Roofing in <span className="text-honey">{city.name}, {city.state}</span>
            </h1>
            <p className="text-lg text-zinc-300 mt-5 max-w-xl leading-relaxed">{city.intro}</p>
            <div className="flex flex-wrap gap-x-6 gap-y-3 mt-6">
              {[{ icon: Star, t: "5.0 Rated Local Crew" }, { icon: ShieldCheck, t: "Licensed & Insured WA" }, { icon: Building2, t: "Commercial & Residential" }].map((b) => (
                <div key={b.t} className="flex items-center gap-2 text-sm text-zinc-200"><b.icon className="w-4 h-4 text-honey" /> {b.t}</div>
              ))}
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <button onClick={openQuote} data-testid="city-quote-btn"
                className="bg-honey text-white font-bold px-6 py-3.5 rounded-sm hover:bg-honey-600 transition-colors">
                Get a Free {city.name} Roof Quote
              </button>
              <a href={`tel:${COMPANY.phoneRaw}`} data-testid="city-call-btn"
                className="border border-white/20 text-white font-semibold px-6 py-3.5 rounded-sm hover:border-honey hover:text-honey transition-colors flex items-center gap-2">
                <Phone className="w-4 h-4 text-honey" /> {COMPANY.phone}
              </a>
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="bg-carbon/80 backdrop-blur-xl border border-white/10 rounded-md p-6 sm:p-8 shadow-2xl">
            <h2 className="text-2xl font-display font-bold text-white">Free {city.name} Roof Evaluation</h2>
            <p className="text-sm text-zinc-400 mt-1 mb-5">Fixed, no-hassle pricing in 30 seconds.</p>
            <QuoteForm />
          </motion.div>
        </div>
      </section>

      {/* Local context */}
      <section className="py-20 bg-carbon border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-honey font-bold mb-3">Built for {city.region}</p>
            <h2 className="text-3xl sm:text-4xl font-display font-black uppercase tracking-tighter text-white">
              Roofing That Stands Up to {city.name} Weather
            </h2>
            <p className="text-zinc-400 mt-4 text-lg leading-relaxed">{city.weather}</p>
            <div className="mt-6">
              <p className="text-sm font-semibold text-white mb-2">Neighborhoods we serve in {city.name}:</p>
              <div className="flex flex-wrap gap-2">
                {city.neighborhoods.map((n) => (
                  <span key={n} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-white/10 bg-obsidian text-xs text-zinc-300">
                    <Check className="w-3 h-3 text-honey" /> {n}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="bg-obsidian border border-white/10 rounded-md p-6">
            <p className="text-sm font-semibold text-white mb-3">Why {city.name} homeowners choose HoneyBee:</p>
            <ul className="space-y-3">
              {[
                "Honest, flat-rate pricing — no surprise charges",
                "50-year CertainTeed shingle guarantee",
                "Free, no-obligation in-home roof evaluation",
                "Storm damage documentation & insurance claim help",
                "Locally owned, licensed & insured in WA",
              ].map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm text-zinc-300">
                  <Check className="w-4 h-4 text-honey mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
            <p className="text-xs text-zinc-500 mt-5">Also serving nearby: {city.nearby.join(" • ")}. ZIP codes: {city.zips.join(", ")}.</p>
          </div>
        </div>
      </section>

      <Services onQuote={openQuote} />
      <WhyChooseUs />
      <Testimonials />
      <FAQ items={city.faqs} title={`${city.name} Roofing FAQs`} eyebrow={`${city.name}, ${city.state}`} />
      <CTABand onQuote={openQuote} />
      <Footer />
      <StickyCallBar onQuote={openQuote} />

      {modal && (
        <div data-testid="quote-modal" data-lenis-prevent onClick={() => setModal(false)}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
          <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
            className="relative bg-carbon border border-white/10 rounded-md p-6 sm:p-8 w-full max-w-md my-8">
            <button onClick={() => setModal(false)} data-testid="quote-modal-close" className="absolute top-4 right-4 text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-display font-bold text-white mb-1">Free {city.name} Roof Evaluation</h2>
            <p className="text-sm text-zinc-400 mb-5">Fixed, no-hassle pricing in 30 seconds.</p>
            <QuoteForm onSuccess={() => {}} />
          </motion.div>
        </div>
      )}
    </div>
  );
}
